// Возрастные группы и нормы по нутриентам

export type AgeGroup = 'adult' | '40+' | '50+' | '60+' | '70+';

export interface HealthFlags {
  kidney_disease?: boolean;       // Заболевание почек / ограничен белок
  osteoporosis?: boolean;          // Остеопороз / остеопения
  dairy_intolerance?: boolean;     // Непереносимость молочных
  vegetarian?: boolean;            // Вегетарианство / веганство
  taking_supplements?: boolean;    // Принимает добавки кальция/D
}

export function getAgeGroup(birthDate: string | null | undefined): AgeGroup {
  if (!birthDate) return 'adult';
  const years = Math.floor(
    (Date.now() - new Date(birthDate).getTime()) / (365.25 * 24 * 3600 * 1000)
  );
  if (years < 40) return 'adult';
  if (years < 50) return '40+';
  if (years < 60) return '50+';
  if (years < 70) return '60+';
  return '70+';
}

export function getAge(birthDate: string | null | undefined): number | null {
  if (!birthDate) return null;
  return Math.floor(
    (Date.now() - new Date(birthDate).getTime()) / (365.25 * 24 * 3600 * 1000)
  );
}

// Дневные ориентиры по нутриентам — зависят от пола и возраста
export interface DailyTargets {
  protein_min_g_per_kg: number;     // минимум г/кг (норма)
  protein_target_g_per_kg: number;  // целевой г/кг для возрастной группы
  calcium_mg: number;
  vitamin_d_mcg: number;
  fiber_g: number;
  b12_mcg: number;
}

export function getDailyTargets(
  ageGroup: AgeGroup,
  sex: 'male' | 'female' | null | undefined,
  weightKg: number
): DailyTargets {
  // Кальций
  let calcium = 1000;
  if (ageGroup === '50+' || ageGroup === '60+' || ageGroup === '70+') {
    calcium = (sex === 'female' || ageGroup === '70+') ? 1300 : 1000;
  }

  // Витамин D
  let vitaminD = 15;
  if (ageGroup === '60+' || ageGroup === '70+') vitaminD = 20;

  // Белок
  let proteinPerKg = 0.83; // ВОЗ
  let proteinTargetPerKg = 0.9;
  if (ageGroup === '50+') { proteinPerKg = 0.9; proteinTargetPerKg = 1.0; }
  if (ageGroup === '60+') { proteinPerKg = 1.0; proteinTargetPerKg = 1.1; }
  if (ageGroup === '70+') { proteinPerKg = 1.0; proteinTargetPerKg = 1.2; }

  return {
    protein_min_g_per_kg: proteinPerKg,
    protein_target_g_per_kg: proteinTargetPerKg,
    calcium_mg: calcium,
    vitamin_d_mcg: vitaminD,
    fiber_g: 28,
    b12_mcg: 2.4,
  };
}

// === ПРАВИЛА И ТРИГГЕРЫ ===

export interface MealHint {
  type: 'warning' | 'praise' | 'info';
  text: string;
  cta?: 'add_protein' | 'add_calcium';
}

// Подсказка по конкретному приёму пищи
export function getMealHint(params: {
  ageGroup: AgeGroup;
  proteinG: number;
  caloriesKcal: number;
  healthFlags?: HealthFlags;
}): MealHint | null {
  const { ageGroup, proteinG, caloriesKcal, healthFlags } = params;

  // Для adult и 40+ — без подсказок (или минимально)
  if (ageGroup === 'adult' || ageGroup === '40+') return null;

  // Если есть ограничение по белку — не советуем добавлять
  const proteinRestricted = healthFlags?.kidney_disease;

  // Хороший белковый приём пищи (25-40г)
  if (proteinG >= 25 && proteinG <= 40 && caloriesKcal >= 200) {
    if (ageGroup === '50+') {
      return {
        type: 'praise',
        text: 'Хороший белковый приём пищи. Для возраста 50+ это сильный плюс для сохранения мышечной массы.',
      };
    }
    if (ageGroup === '60+' || ageGroup === '70+') {
      return {
        type: 'praise',
        text: 'Отличный приём пищи: достаточно белка для поддержки мышц и силы в вашем возрасте.',
      };
    }
  }

  // Очень богатый белком (40+)
  if (proteinG >= 40) {
    return {
      type: 'praise',
      text: 'Отличный источник белка — поддерживает мышцы и сытость.',
    };
  }

  // Маленький приём пищи с малым белком (триггер «булочка с чаем»)
  if (proteinG < 10 && caloriesKcal >= 100 && !proteinRestricted) {
    if (ageGroup === '60+' || ageGroup === '70+') {
      return {
        type: 'warning',
        text: 'В этом приёме мало белка. После 60 лет лучше добавлять источник белка: творог, яйцо, рыбу, мясо, йогурт или бобовые.',
        cta: 'add_protein',
      };
    }
    if (ageGroup === '50+') {
      return {
        type: 'warning',
        text: 'В этом приёме мало белка. После 50 лет лучше чаще добавлять белок в основные приёмы — это помогает поддерживать мышцы и сытость.',
        cta: 'add_protein',
      };
    }
  }

  return null;
}

// Дневной возрастной фокус
export interface DayFocus {
  ageGroup: AgeGroup;
  protein: { value: number; target: number; status: 'good' | 'low' | 'great'; text: string };
  fiber: { value: number; target: number; status: 'good' | 'low'; text: string };
  general?: string;
  hints: string[]; // Общие подсказки на день
}

export function getDayFocus(params: {
  ageGroup: AgeGroup;
  sex: 'male' | 'female' | null | undefined;
  weightKg: number;
  totalProteinG: number;
  totalFiberG?: number;
  totalCaloriesKcal: number;
  targetCaloriesKcal: number;
  healthFlags?: HealthFlags;
}): DayFocus | null {
  const { ageGroup, sex, weightKg, totalProteinG, totalCaloriesKcal, targetCaloriesKcal, healthFlags } = params;

  // Только для 50+
  if (ageGroup === 'adult' || ageGroup === '40+') return null;

  const targets = getDailyTargets(ageGroup, sex, weightKg);
  const proteinMin = Math.round(targets.protein_min_g_per_kg * weightKg);
  const proteinTarget = Math.round(targets.protein_target_g_per_kg * weightKg);

  // Если есть ограничение по белку — корректируем
  const proteinRestricted = healthFlags?.kidney_disease;

  // Статус белка
  let proteinStatus: 'good' | 'low' | 'great' = 'low';
  let proteinText = '';
  
  if (proteinRestricted) {
    proteinStatus = 'good';
    proteinText = 'Рекомендации по белку лучше согласовать с врачом, так как отмечено ограничение по почкам.';
  } else if (totalProteinG >= proteinTarget) {
    proteinStatus = 'great';
    proteinText = `Отлично! ${Math.round(totalProteinG)}г — для вашего возраста это хороший показатель для поддержки мышц.`;
  } else if (totalProteinG >= proteinMin) {
    proteinStatus = 'good';
    proteinText = `${Math.round(totalProteinG)}г белка — на уровне нормы. Можно стремиться к ${proteinTarget}г для лучшей поддержки мышечной массы.`;
  } else {
    proteinStatus = 'low';
    proteinText = `Белка сегодня маловато: ${Math.round(totalProteinG)}г. Для вашего возраста лучше ${proteinTarget}г — добирай в каждом основном приёме.`;
  }

  // Подсказки на день
  const hints: string[] = [];

  // Если калорий мало для 60+
  if ((ageGroup === '60+' || ageGroup === '70+') && totalCaloriesKcal > 0 && totalCaloriesKcal < targetCaloriesKcal * 0.7) {
    hints.push('Сегодня рацион выглядит слишком лёгким. В старшем возрасте важно не только «не переедать», но и не недоедать: белок, кальций и энергия нужны для силы и восстановления.');
  }

  // Кальций — если не молочная непереносимость
  if (!healthFlags?.dairy_intolerance) {
    hints.push(`Кальций сегодня может быть низким (ориентир ~${targets.calcium_mg} мг). Можно добавить йогурт, творог, сыр, кефир, сардины с костями, тофу или зелёные овощи.`);
  } else {
    hints.push('При непереносимости молочного источники кальция: сардины с костями, тофу с кальцием, обогащённые растительные напитки, кейл, брокколи.');
  }

  // Витамин D
  hints.push(`Витамин D трудно стабильно закрывать только едой (ориентир ~${targets.vitamin_d_mcg} мкг). Хорошие источники: жирная рыба, яйца, обогащённое молоко. При подозрении на дефицит — обсудить анализ 25(OH)D с врачом.`);

  // B12 для вегетарианцев
  if (healthFlags?.vegetarian) {
    hints.push('B12 особенно важен при растительной диете. Если не ешь животные продукты — обсуди с врачом приём добавок B12.');
  }

  return {
    ageGroup,
    protein: {
      value: Math.round(totalProteinG),
      target: proteinTarget,
      status: proteinStatus,
      text: proteinText,
    },
    fiber: {
      value: 0, // пока не считаем — в будущем можно добавить
      target: 28,
      status: 'low',
      text: '',
    },
    hints,
  };
}

// === КАТАЛОГ БЕЛКОВЫХ ДОБАВОК ===

export interface ProteinAddition {
  emoji: string;
  name: string;
  weight_g: number;
  calories: number;
  protein_g: number;
  fat_g: number;
  carbs_g: number;
}

export const PROTEIN_ADDITIONS: ProteinAddition[] = [
  { emoji: '🥚', name: 'Яйцо варёное', weight_g: 60, calories: 90, protein_g: 7.5, fat_g: 6.2, carbs_g: 0.6 },
  { emoji: '🥚', name: '2 яйца варёных', weight_g: 120, calories: 180, protein_g: 15, fat_g: 12.4, carbs_g: 1.2 },
  { emoji: '🥛', name: 'Творог 5%', weight_g: 150, calories: 180, protein_g: 27, fat_g: 7.5, carbs_g: 4.5 },
  { emoji: '🥛', name: 'Греческий йогурт', weight_g: 150, calories: 90, protein_g: 15, fat_g: 0.6, carbs_g: 6 },
  { emoji: '🐟', name: 'Рыба запечённая', weight_g: 150, calories: 240, protein_g: 32, fat_g: 12, carbs_g: 0 },
  { emoji: '🥩', name: 'Курица варёная', weight_g: 100, calories: 165, protein_g: 31, fat_g: 3.6, carbs_g: 0 },
  { emoji: '🫘', name: 'Фасоль варёная', weight_g: 150, calories: 195, protein_g: 13, fat_g: 0.7, carbs_g: 35 },
  { emoji: '🧀', name: 'Сыр твёрдый', weight_g: 40, calories: 160, protein_g: 10, fat_g: 13, carbs_g: 0 },
];

// === КАТАЛОГ КАЛЬЦИЯ ===

export const CALCIUM_ADDITIONS: ProteinAddition[] = [
  { emoji: '🥛', name: 'Творог 5%', weight_g: 150, calories: 180, protein_g: 27, fat_g: 7.5, carbs_g: 4.5 },
  { emoji: '🥛', name: 'Греческий йогурт', weight_g: 150, calories: 90, protein_g: 15, fat_g: 0.6, carbs_g: 6 },
  { emoji: '🥛', name: 'Кефир', weight_g: 200, calories: 110, protein_g: 6, fat_g: 4, carbs_g: 10 },
  { emoji: '🧀', name: 'Сыр твёрдый', weight_g: 40, calories: 160, protein_g: 10, fat_g: 13, carbs_g: 0 },
  { emoji: '🐟', name: 'Сардины с костями', weight_g: 100, calories: 210, protein_g: 25, fat_g: 11, carbs_g: 0 },
  { emoji: '🥦', name: 'Брокколи варёная', weight_g: 150, calories: 50, protein_g: 4, fat_g: 0.6, carbs_g: 10 },
];

// === Возрастная подсказка к ПРОДУКТУ при сканировании ===

export interface ProductHint {
  type: 'highlight' | 'praise' | 'caution';
  text: string;
}

// Определяем "приоритетные" продукты по ключевым словам
const PROTEIN_RICH_KEYWORDS = ['творог', 'йогурт', 'кефир', 'яйц', 'курица', 'индейка', 'рыба', 'лосось', 'тунец', 'сардин', 'скумбри', 'говядина', 'печень', 'фасоль', 'нут', 'чечевиц', 'тофу', 'сыр'];
const CALCIUM_RICH_KEYWORDS = ['творог', 'йогурт', 'кефир', 'молок', 'сыр', 'сардин', 'тофу', 'брокколи', 'кейл'];
const OMEGA3_RICH_KEYWORDS = ['лосось', 'скумбри', 'сардин', 'сельдь', 'форель', 'тунец'];

export function getProductHint(params: {
  ageGroup: AgeGroup;
  productName: string;
  proteinG: number;
  weightG: number;
}): ProductHint | null {
  const { ageGroup, productName, proteinG, weightG } = params;
  const name = productName.toLowerCase();

  if (ageGroup === 'adult' || ageGroup === '40+') return null;

  const isProteinRich = PROTEIN_RICH_KEYWORDS.some(k => name.includes(k)) && proteinG / weightG > 0.10;
  const isCalciumRich = CALCIUM_RICH_KEYWORDS.some(k => name.includes(k));
  const isOmega3Rich = OMEGA3_RICH_KEYWORDS.some(k => name.includes(k));

  // Лосось/скумбрия/сардины — омега-3 для 60+
  if (isOmega3Rich && (ageGroup === '60+' || ageGroup === '70+')) {
    return {
      type: 'praise',
      text: 'Отличный продукт для 60+: белок, омега-3 и витамин D — поддержка мышц, костей и сердечно-сосудистой системы.',
    };
  }

  // Творог/йогурт — белок + кальций для 50+
  if (isProteinRich && isCalciumRich) {
    return {
      type: 'praise',
      text: 'Хороший выбор: белок и кальций — два важных нутриента для возраста 50+.',
    };
  }

  // Просто белок-плотный продукт
  if (isProteinRich) {
    return {
      type: 'highlight',
      text: 'Хороший источник белка — важно для поддержки мышц после 50 лет.',
    };
  }

  return null;
}
